import {
  createRootRouteWithContext,
  createRoute,
  redirect,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { RootLayout } from './routes/root';
import { InventoryList, InventoryListSearchSchema } from './routes/index';
import { NewListing } from './routes/new';
import { EditListing, editLoader } from './routes/edit';
import { PartList, PartListSearchSchema } from './routes/parts-index';
import { NewPart } from './routes/parts-new';
import { EditPart, editPartLoader } from './routes/parts-edit';
import { ProductList, ProductListSearchSchema } from './routes/products-index';
import { NewProduct } from './routes/products-new';
import { EditProduct, editProductLoader } from './routes/products-edit';
import { SettingsLayout } from './routes/settings';
import { LookupsPage } from './routes/lookups';
import { AuditPage } from './routes/audit';
import { ImportPage } from './routes/import';
import { SheetPage } from './routes/sheet';

export const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: InventoryList,
  validateSearch: InventoryListSearchSchema.parse,
});

const newRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/new',
  component: NewListing,
});

const editRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/edit/$id',
  loader: editLoader,
  component: EditListing,
});

const partsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/parts',
  component: PartList,
  validateSearch: PartListSearchSchema.parse,
});

const newPartRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/parts/new',
  component: NewPart,
});

const editPartRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/parts/edit/$id',
  loader: editPartLoader,
  component: EditPart,
});

const productsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products',
  component: ProductList,
  validateSearch: ProductListSearchSchema.parse,
});

const newProductRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products/new',
  component: NewProduct,
});

const editProductRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products/edit/$id',
  loader: editProductLoader,
  component: EditProduct,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsLayout,
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/settings/lookups' });
  },
});

const settingsLookupsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'lookups',
  component: LookupsPage,
});

const settingsAuditRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'audit',
  component: AuditPage,
});

const settingsImportRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'import',
  component: ImportPage,
});

const settingsSheetRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'sheet',
  component: SheetPage,
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  newRoute,
  editRoute,
  partsRoute,
  newPartRoute,
  editPartRoute,
  productsRoute,
  newProductRoute,
  editProductRoute,
  settingsRoute.addChildren([
    settingsIndexRoute,
    settingsLookupsRoute,
    settingsAuditRoute,
    settingsImportRoute,
    settingsSheetRoute,
  ]),
]);
